import { useMemo } from 'react';
import { Path } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import * as zod from 'zod';
import { Contact } from '@lgg/isomorphic/types/__generated__/graphql';

export const contactFormSchema = zod.object({
  firstName: zod.string().min(1).max(100),
  lastName: zod.string().min(1).max(100),
  primaryEmail: zod.email().nullable(),
  primaryPhone: zod.string().nullable(),
  interest: zod.string().nullable(),
  secondaryPhone: zod.string().nullable(),
  gender: zod.enum(['M', 'F']).nullable(),
  birthdate: zod.string().nullable(),
  campaignId: zod.number().nullable(),
  ownerId: zod.number().nullable(),
  sourceId: zod.number().nullable(),
  statusId: zod.number().default(1),
  address: zod
    .object({
      address: zod.string().nullable(),
      city: zod.string().nullable(),
      country: zod.string().nullable(),
      postalCode: zod.string().nullable(),
      state: zod.string().nullable(),
    })
    .nullable(),
  tags: zod.array(zod.string()).nullish(),
});

export type ContactFormValues = zod.infer<typeof contactFormSchema>;

// Free-text inputs autosaved when the user leaves them rather than while typing.
export const CONTACT_AUTOSAVE_ON_BLUR_FIELDS: Path<ContactFormValues>[] = [
  'firstName',
  'lastName',
  'primaryEmail',
  'interest',
  'primaryPhone',
  'secondaryPhone',
];

export const addressDefaultValue: NonNullable<ContactFormValues['address']> = {
  address: null,
  city: null,
  state: null,
  country: null,
  postalCode: null,
};

export const getContactDefaultValues = (contact: Contact): ContactFormValues => {
  const {
    firstName,
    lastName,
    primaryEmail,
    primaryPhone,
    interest,
    secondaryPhone,
    tags,
    campaign,
    source,
    assignee,
    status,
    address1,
    addressCountry,
    addressState,
    postalCode,
    city,
    sex,
    birthdate,
  } = contact;

  return {
    firstName: firstName ?? '',
    lastName: lastName ?? '',
    primaryEmail: primaryEmail ?? null,
    primaryPhone: primaryPhone?.e164 ?? null,
    interest: interest ?? '',
    secondaryPhone: secondaryPhone?.e164 ?? null,
    statusId: parseInt(status?.id ?? '1'),
    gender: (sex as 'M' | 'F') ?? null,
    birthdate: birthdate ?? null,
    campaignId: campaign?.id ?? null,
    sourceId: source?.id ?? null,
    ownerId: assignee?.id ?? null,
    tags: tags?.map((tag) => tag.id.toString()) ?? null,
    address: {
      address: address1 ?? null,
      city: city ?? null,
      country: addressCountry ?? null,
      state: addressState ?? null,
      postalCode: postalCode ?? null,
    },
  };
};

export enum PossibleExtraFields {
  SECONDARY_PHONE = 'SECONDARY_PHONE',
  BIRTHDATE = 'BIRTHDATE',
  GENDER = 'GENDER',
  ADDRESS = 'ADDRESS',
  CAMPAIGN = 'CAMPAIGN',
  SOURCE = 'SOURCE',
}

export const getPossibleExtraFields = (
  values: ContactFormValues,
): PossibleExtraFields[] => {
  const { secondaryPhone, gender, birthdate, campaignId, sourceId, address } = values;

  const fields: PossibleExtraFields[] = [];

  if (secondaryPhone) {
    fields.push(PossibleExtraFields.SECONDARY_PHONE);
  }

  if (gender) {
    fields.push(PossibleExtraFields.GENDER);
  }

  if (birthdate) {
    fields.push(PossibleExtraFields.BIRTHDATE);
  }

  if (campaignId) {
    fields.push(PossibleExtraFields.CAMPAIGN);
  }

  if (sourceId) {
    fields.push(PossibleExtraFields.SOURCE);
  }

  if (address && Object.values(address).some(Boolean)) {
    fields.push(PossibleExtraFields.ADDRESS);
  }

  return fields;
};

export const useContactFormResolver = () => {
  const { t } = useTranslation(['contacts']);

  return useMemo(
    () =>
      zodResolver(
        contactFormSchema.extend({
          firstName: zod
            .string()
            .min(1, t('contacts:createContactModal.fields.firstName.errors.required'))
            .max(100, t('contacts:createContactModal.fields.firstName.errors.maxLength')),
          lastName: zod
            .string()
            .min(1, t('contacts:createContactModal.fields.lastName.errors.required'))
            .max(100, t('contacts:createContactModal.fields.lastName.errors.maxLength')),
          primaryEmail: zod
            .email(t('contacts:createContactModal.fields.email.errors.format'))
            .or(zod.literal(''))
            .nullable(),
        }),
      ),
    [t],
  );
};
